/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { nanoid } from 'nanoid';
import BillPaperToPrintPopup from './printing-forms/ModalBillPaperToPrint';

const DISCOUNT_TYPES = {
  END_BILL: 'endBill',
  COUPON: 'coupon',
  PROMOTION: 'promotion',
  POINT: 'point'
};

export default function BillDetail({ staffObj, selectedUserObj, userInfoObj, discountObj, paymentObj }) {
  const billData = useSelector(state => state.bill.data);
  const paymentResponse = useSelector(state => state.bill.paymentResponse);
  const [printPopupOpen, setPrintPopupOpen] = useState(false);

  const [paymentPreview, setPaymentPreview] = useState({
    items: [],
    total: 0,
    serviceCharge: 0,
    finalAmount: 0
  });

  const formatAmount = useCallback(num => {
    const numStr = (num ?? 0).toString();
    const [intPart, decimalPart = ''] = numStr.split('.');
    let formattedDecimal;
    if (decimalPart.length === 0) {
      formattedDecimal = '00';
    } else if (decimalPart.length === 1) {
      formattedDecimal = decimalPart + '0';
    } else {
      formattedDecimal = decimalPart.substring(0, 2);
    }
    return `${intPart}.${formattedDecimal}`;
  }, []);

  const handlePrintButtonClick = () => {
    setPrintPopupOpen(true);
  };

  const handlePrintComplete = () => {
    setPrintPopupOpen(false);
  };

  useEffect(() => {
    if (paymentObj && paymentObj.data) {
      const payments = Array.isArray(paymentObj.data) ? paymentObj.data : [];

      const paymentTotal = payments.reduce((sum, payment) => {
        return sum + parseFloat(payment.amount || 0);
      }, 0);

      setPaymentPreview({
        items: payments,
        total: paymentTotal,
        serviceCharge: billData?.service_charge || 0,
        finalAmount: parseFloat(billData?.final_amount || billData?.grand_total || 0)
      });
    }
  }, [paymentObj, billData]);

  if (!billData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center flex-column"
        style={{ height: 'calc(100vh - 370px)' }}
      >
        <div className="alert alert-warning">ไม่พบข้อมูลบิล กรุณาลองใหม่อีกครั้ง</div>
      </div>
    );
  }

  const orderNo = billData.order?.code || '';
  const orderDate = billData.created_at
    ? new Date(billData.created_at).toLocaleString('th-TH')
    : new Date().toLocaleString('th-TH');

  const businessData = billData.order?.branchPosSelling?.branchPos?.branch?.business;
  const entityTypeId = businessData?.entity_type_id;

  let businessName = '';
  if (entityTypeId === 1) {
    businessName = businessData?.company_name || '';
  } else if (entityTypeId === 2) {
    businessName = `${businessData?.first_name || ''} ${businessData?.last_name || ''}`.trim();
  } else {
    businessName = businessData?.company_name || '';
  }

  const branchName = billData.order?.branchPosSelling?.branchPos?.branch?.branch_name || '';
  const posName = billData.order?.branchPosSelling?.branchPos?.name || '';
  const posId = billData.order?.branchPosSelling?.branchPos?.pos_id || '';
  const staffName = billData.userBusinessGroup
    ? `${billData.userBusinessGroup.first_name || ''} ${billData.userBusinessGroup.last_name || ''}`.trim()
    : '';

  const displayProducts = (billData.billItems || []).map(item => {
    const orderProductOptions = item.orderItem?.orderProductOption || [];
    const orderProductSpecificOptions = item.orderItem?.orderProductSpecificOption || [];
    const orderProductAnnotations = item.orderItem?.orderProductAnnotation || [];

    return {
      id: item.id,
      productName: item.orderItem?.product?.name || item.orderItem?.item_name || '',
      productQty: item.quantity,
      productPrice: item.price,
      productSumPrice: item.price || 0,
      productStatus: 1,
      note: item.orderItem?.remark || '',

      productOptions: orderProductOptions.map(option => ({
        itemName: option.productOptionItem?.name || '',
        itemPrice: parseFloat(option.qty || '0') * parseFloat(option.productOptionItem?.price || '0')
      })),
      specificOptions: orderProductSpecificOptions.map(option => ({
        itemName: option.productSpecificOptionItem?.name || '',
        itemPrice: parseFloat(option.qty || '0') * parseFloat(option.productSpecificOptionItem?.price || '0')
      })),
      annotationOptions: orderProductAnnotations.map(annotation => ({
        itemName: annotation.productAnnotationItem?.name || ''
      }))
    };
  });

  const renderProductOptions = item => {
    return (
      <div className="ps-3 my-1">
        {item.productOptions?.map((option, idx) => (
          <div className="mb-1" key={`${item.id}-product-${idx}`}>
            - {option.itemName}
            {option.itemPrice > 0 && ` (+${formatAmount(option.itemPrice)})`}
          </div>
        ))}

        {item.specificOptions?.map((option, idx) => (
          <div className="mb-1" key={`${item.id}-specific-${idx}`}>
            - {option.itemName}
            {option.itemPrice > 0 && ` (+${formatAmount(option.itemPrice)})`}
          </div>
        ))}
      </div>
    );
  };

  const renderProductAnnotations = item => {
    return (
      <>
        {item.annotationOptions?.length > 0 && (
          <div>
            {item.annotationOptions.map((option, idx) => (
              <span key={`${item.id}-annotation-${idx}`}>
                {option.itemName}
                {idx < item.annotationOptions.length - 1 && ', '}
              </span>
            ))}
          </div>
        )}

        {item.note && <div className="mt-1 text-muted">หมายเหตุ: {item.note}</div>}
      </>
    );
  };

  const getMemberInfo = () => {
    if (!discountObj?.getByType) return null;

    const pointDiscount = discountObj.getByType(DISCOUNT_TYPES.POINT);

    if (pointDiscount?.member_id) {
      return {
        id: pointDiscount.member_id,
        name: userInfoObj?.userInfo?.fullName || '',
        code: pointDiscount.code || '',
        pointUsed: pointDiscount.point || 0
      };
    }

    return null;
  };

  const memberInfo = getMemberInfo();

  const remainingAmount = paymentPreview.total
    ? formatAmount(billData.final_amount - paymentPreview.total)
    : formatAmount(billData.final_amount);

  return (
    <div>
      <BillPaperToPrintPopup
        isOpen={printPopupOpen}
        onPrintComplete={handlePrintComplete}
        paymentResponse={paymentResponse}
      />

      <div className="pe-2 bx-scroll overflow-auto mb-2" style={{ height: 'calc(100vh - 370px)' }}>
        <div className="bg-[#F4F5F6] p-4" style={{ minHeight: 'calc(100vh - 370px)' }}>
          <div className="text-center fs-24 text-[#4A4A4A]">{businessName}</div>
          <div className="text-center text-[#4A4A4A] fs-14 fw-500 mb-4">Order: {orderNo}</div>

          <div className="d-flex align-items-center fs-14 mb-1">
            <div className="me-auto">สาขา:</div>
            <div>{branchName}</div>
          </div>
          <div className="d-flex align-items-center fs-14 mb-1">
            <div className="me-auto">POS ID:</div>
            <div>
              {posName} : {posId}
            </div>
          </div>
          <div className="d-flex align-items-center fs-14 mb-1">
            <div className="me-auto">ชื่อพนักงาน:</div>
            <div>{staffName || (userInfoObj?.userInfo ? userInfoObj.userInfo.fullName : '-')}</div>
          </div>
          <div className="d-flex align-items-center fs-14 mb-3">
            <div className="me-auto">เวลา:</div>
            <div>{orderDate}</div>
          </div>

          <hr className="my-2" />

          {displayProducts
            .filter(item => item.productStatus !== 4)
            .map((item, index) => (
              <div className="d-flex align-items-start mb-2 fs-14" key={item.id || `product-${index}`}>
                <div className="flex-fill">
                  <div>
                    {item.productQty} x {item.productName}
                  </div>

                  {renderProductOptions(item)}

                  <div>{renderProductAnnotations(item)}</div>
                </div>

                <div>{formatAmount(item.productSumPrice || item.productPrice || 0)}</div>
              </div>
            ))}

          <hr className="my-2" />

          <div className="d-flex align-items-center fs-14 mb-1">
            <div className="me-auto">ยอดรวมก่อนส่วนลด</div>
            <div>{formatAmount(billData.total_before_discount)}</div>
          </div>

          <div className="d-flex align-items-center fs-12 mb-1">
            <div className="me-auto">ส่วนลด</div>
            <div>{formatAmount(billData.discount_amount)}</div>
          </div>

          <div className="d-flex align-items-center fs-12 mb-1">
            <div className="me-auto">ส่วนลดคะแนน</div>
            <div>{formatAmount(billData.discount_points)}</div>
          </div>

          <div className="d-flex align-items-center fs-12 mb-1">
            <div className="me-auto">ยอดขายหลังหักส่วนลด</div>
            <div>{formatAmount(billData.total_after_discount)}</div>
          </div>

          <div className="d-flex align-items-center fs-12 mb-1">
            <div className="me-auto">
              Service Charge {billData.service_charge_percent ? `(${billData.service_charge_percent}%)` : ''}
            </div>
            <div>{formatAmount(billData.service_charge)}</div>
          </div>

          <div className="d-flex align-items-center fs-12 mb-1">
            <div className="me-auto">มูลค่าสินค้าไม่รวมภาษี</div>
            <div>{formatAmount(billData.taxable_amount)}</div>
          </div>

          <div className="d-flex align-items-center fs-12 mb-1">
            <div className="me-auto">ภาษีมูลค่าเพิ่ม</div>
            <div>{formatAmount(billData.vat)}</div>
          </div>

          <div className="d-flex align-items-center fs-14 mb-3 font-weight-bold">
            <div className="me-auto">ราคาสุทธิ</div>
            <div>{formatAmount(billData.final_amount)}</div>
          </div>

          <div className="d-flex align-items-center fs-12 mb-1">
            <div className="me-auto">จำนวนเงินปัดเศษ</div>
            <div>{formatAmount(billData.loss_rounded)}</div>
          </div>

          <div className="d-flex align-items-center fs-14 mb-3 font-weight-bold">
            <div className="me-auto">จำนวนที่ต้องชำระ</div>
            <div>{formatAmount(billData.final_profit_loss_rounded)}</div>
          </div>

          {paymentPreview.items && paymentPreview.items.length > 0 && (
            <>
              <hr className="my-2" />
              <div className="d-flex align-items-center fs-14 mb-1 text-[#2DC76D]">
                <div className="me-auto">ยอดชำระแล้ว</div>
                <div>{formatAmount(paymentPreview.total)}</div>
              </div>
              <div className="d-flex align-items-center fs-14 mb-1 text-[#F95057]">
                <div className="me-auto">ยอดคงเหลือ</div>
                <div>{remainingAmount}</div>
              </div>
            </>
          )}

          {memberInfo && (
            <>
              <hr className="my-3" />
              <div className="text-center mb-2">
                <div className="fs-12">Member : {memberInfo.code || 'MB' + nanoid(4)}</div>
                <div className="fs-12">คุณ : {memberInfo.name}</div>
              </div>

              <div className="d-flex justify-content-center text-center fs-12 mb-2">
                <div className="flex-fill">ใช้คะแนน</div>
              </div>

              <div className="d-flex justify-content-center text-center fs-12 mb-3">
                <div className="flex-fill">{memberInfo.pointUsed || '-'}</div>
              </div>
            </>
          )}
          <hr className="my-2" />
          <div className="text-center mt-4 fs-12">
            <div>THANK YOU</div>
            <div>ขอบคุณที่ใช้บริการ</div>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center pt-2">
        {userInfoObj?.userInfo ? (
          <div className="card-staff me-auto">
            <div className="flex gap-[min(1.46vw,15px)]">
              <img
                className="aspect-square w-[min(3.91vw,40px)] rounded-full object-contain"
                src="/img/avartar-male.png"
                alt=""
              />
              <div className="text-[#4c4e64] opacity-87 leading-[1.4] text-14 font-[400] tracking-[0.015em]">
                <div>{userInfoObj.userInfo.fullName}</div>
                <div className="opacity-60">{userInfoObj.userInfo.code}</div>
              </div>

              <button
                type="button"
                className="btn btn-edit-st-2 align-self-end"
                onClick={() => staffObj.setStaffSelect(true)}
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="btn me-auto btn-x1 text-[#FFF] bg-[#2DC76D]"
            onClick={() => {
              selectedUserObj.setSelectedUser(userInfoObj);
              staffObj.setStaffSelect(true);
            }}
          >
            เพิ่มพนักงานขาย
          </button>
        )}

        <button
          type="button"
          className="btn btn-x1 btn-x1-print pe-4 ps-5 text-[#000]"
          onClick={handlePrintButtonClick}
        >
          พิมพ์
        </button>
      </div>
    </div>
  );
}
